import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, doc, getDoc, where } from 'firebase/firestore';
import { db } from './firebase';
import UserProfile from './UserProfile';
import config from '../config';
import './ChatInterface.css'; // Import the CSS file
import { Loading } from 'react-loading-dot'
import { loadStripe } from '@stripe/stripe-js';
import useAuthTokenRedirect from './useAuthTokenRedirect';

interface Message {
    sender: 'user' | 'assistant';
    text: string;
}

interface ChatInterfaceProps {
    userId: string | null;
}

function isAxiosError(error: unknown): error is { response?: { data: unknown }; request?: unknown; message: unknown } {
    return !!error && typeof error === 'object' && ('response' in error || 'request' in error || 'message' in error);
}


const ChatInterface: React.FC<ChatInterfaceProps> = ({ userId }) => {

    //Check Sign In
    useAuthTokenRedirect(userId);

    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [hasPaid, setHasPaid] = useState(true);
    const [hasCheckPaid, setHasCheckPaid] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isUserProfileCollapsed, setIsUserProfileCollapsed] = useState(true);
    const chatBodyRef = useRef<HTMLDivElement>(null); // Explicitly set the type to HTMLDivElement
    // Add a new piece of state for the dropdown selection
    const [astrologyType, setAstrologyType] = useState('western');

    const [isLoading, setIsLoading] = useState(false); // State to control loading icon

    const stripePromise = loadStripe('pk_live_51KQICtKv1Ym6a69nqS6lcOj4DOuqfXLy3qHPrQub2RpF446uDl2JVqGB1SOKwlEdxURB25WRM9E6KV8jdJwLJ1WP00ryHDeqvb');

    const createOneTimePaymentSession = async () => {
        try {
            const response = await axios.post(`${config.apiUrl}/create-3-messages-checkout-session`, {
                userId: userId
            });
            if (response.status === 200) {
                const sessionId = response.data.id;
                const stripe = await stripePromise;
                if (stripe) {
                    stripe.redirectToCheckout({
                        sessionId: sessionId
                    }).then(function (result) {
                        if (result.error) {
                            console.error('Stripe redirect failed:', result.error.message);
                        }
                    });
                } else {
                    console.error('Stripe failed to initialize.');
                }
            }
        } catch (error: unknown) {
            if (isAxiosError(error)) {
                if (error.response) {
                    console.error('Server Response:', error.response.data);
                } else if (error.request) {
                    console.error('No Server Response:', error.request);
                } else {
                    console.error('Error:', error.message);
                }
            } else {
                console.error('An unknown error occurred:', error);
            }
        }


    };


    const createMonthlySubscriptionSession = async () => {
        try {
            const response = await axios.post(`${config.apiUrl}/create-monthly-subscription`, {
                userId: userId
            });
            if (response.status === 200) {
                const sessionId = response.data.id;
                const stripe = await stripePromise;
                if (stripe) {
                    stripe.redirectToCheckout({
                        sessionId: sessionId
                    }).then(function (result) {
                        if (result.error) {
                            console.error('Stripe redirect failed:', result.error.message);
                        }
                    });
                } else {
                    console.error('Stripe failed to initialize.');
                }
            }
        } catch (error: unknown) {
            console.error('Error creating monthly subscription session:', error);
            if (isAxiosError(error)) {
                if (error.response) {
                    console.error('Server Response:', error.response.data);
                } else if (error.request) {
                    console.error('No Server Response:', error.request);
                } else {
                    console.error('Error:', error.message);
                }
            } else {
                console.error('An unknown error occurred:', error);
            }
        }
    };

    useEffect(() => {
        // Reset zoom level to 1 when transitioning to this page
        document.querySelector('meta[name=viewport]')!.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');
    }, []);


    const checkPaymentStatus = async () => {
        let paidStatus = true; // Local variable to track paid status
        setHasCheckPaid(false);
        if (userId) {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                console.log(userData?.messages);
                if (userData?.hasPaidSubscription ||
                    (userData?.hasPaidSingle && userData?.messages >= 1) ||
                    (userData?.messages >= 1)) {
                    paidStatus = true;

                } else {
                    paidStatus = false;
                }
                console.log(hasPaid);
            }
        }
        setHasCheckPaid(true);
        setHasPaid(paidStatus); // Update state
        return paidStatus; // Return local variable
    };
    useEffect(() => {
        const messagesCollection = collection(db, 'messages');
        const q = query(messagesCollection, orderBy('timestamp'), where('userId', '==', userId));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(doc => doc.data() as Message));
        });

        //checkPaymentStatus();

        return unsubscribe;
    }, [userId]);


    useEffect(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, [messages]); // Scroll to bottom whenever messages change



    // useEffect to show popup when hasPaid changes to false
    useEffect(() => {
        if (!hasPaid) {
            setShowModal(true);
        } else {
            setShowModal(false);
        }
    }, [hasPaid]);


    const handleSend = async () => {

        setIsLoading(true); // Show loading icon
        const paymentAllowed = await checkPaymentStatus();

        if (!paymentAllowed) {
            setShowModal(true); // Show modal if not paid
            setIsLoading(false); // Stop loading
            return; // Exit function to prevent sending message
        } else {
            setShowModal(false); // Show modal if not paid

        }

        if (inputValue.trim()) {
            // Proceed with sending the message if input is not empty
            const newMessage: Message = { sender: 'user', text: inputValue.trim() };
            setMessages([...messages, newMessage]);
            setInputValue('');

            await addDoc(collection(db, 'messages'), {
                ...newMessage,
                timestamp: serverTimestamp(),
                userId,
                astrologyType
            });
            // Determine the endpoint based on the astrology type
            const endpoint = astrologyType === 'western' ? '/send-message-western' : '/send-message';

            try {
                const response = await axios.post(`${config.apiUrl}/sendMessage`, {
                    message: newMessage,
                    userId: userId,
                    astrologyType: astrologyType
                }, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                if (response.status === 200) {

                    // No need to add the assistant's reply here.
                    // Firebase's real-time update will handle it.
                    setIsLoading(false);
                }
            } catch (error: any) { // You can use 'any' or a more specific type
                if (axios.isAxiosError(error)) { // Type guard to make sure it's an Axios error
                    console.error('Axios error details:', error.response?.data);
                } else {
                    console.error('An unknown error occurred:', error);
                }
            }
        }


        console.log('has paid:', hasPaid);
    };

    return (
        <div>
            <div className="chat-header">
                <h1>
                    Chat with <select
                        className="astrology-dropdown"
                        value={astrologyType}
                        onChange={(e) => setAstrologyType(e.target.value)}
                    >
                        <option value="western">Western</option>
                        <option value="vedic">Vedic</option>
                    </select> Astrologer
                </h1>

            </div>

            <h4> Please fill out User Profile for best experience </h4>
            <button onClick={() => setIsUserProfileCollapsed(!isUserProfileCollapsed)}>
                {isUserProfileCollapsed ? "Expand User Profile" : "Collapse User Profile"}
            </button>
            <div className={`userProfile-container ${isUserProfileCollapsed ? 'collapsed' : ''}`}>
                <UserProfile userId={userId}></UserProfile>
            </div>
            <div className="chat-container">
                <div className="chat-header">
                    Chat with <span className="astrology-type">{astrologyType === 'western' ? 'Western' : 'Vedic'}</span> Astrologer
                </div>

                <div className="chat-body" ref={chatBodyRef}>
                    {messages.map((message, index) => (
                        <div key={index} className={`chat-message ${message.sender}`}>
                            {message.text}
                        </div>
                    ))}
                    {isLoading &&
                        <div style={{ position: 'absolute', bottom: '0', left: '0', zIndex: '1' }}>
                            <div style={{ position: 'static', top: 'auto', transform: 'none' }}>
                                <Loading margin="0.8rem" size="0.8rem" background="var(--text-color)" />
                            </div>
                        </div>} {/* Add loading icon */}
                </div>
                <div className="chat-footer">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Type your message..."
                    />
                    <button onClick={handleSend}>Send</button>
                </div>
                {showModal &&
                    <div className="modal">
                        <div className="modal-content">
                            <button className="close-button" onClick={() => setShowModal(false)}>X</button>
                            <div className="modal-header">
                                <h4>Please make a payment to continue using this service.</h4>
                            </div>
                            <div className="modal-actions">
                                <button onClick={createOneTimePaymentSession}>One-time payment of 50 Indian rupees for 10 messages</button>
                                <button onClick={createMonthlySubscriptionSession}>30 DAY FREE TRIAL with unlimited messages at 20 rupees ($0.25 USD) / month</button>
                            </div>
                        </div>
                    </div>
                }

            </div>
        </div>
    );
};

export default ChatInterface;